/**
 * Reports always show a date AND a time — Date.toLocaleString() alone prints
 * only the date on some Android builds, so the parts are spelled out.
 */
export function formatReportDateTime(value?: string | Date | null, locale?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  } catch {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }
}

/** Time of day only (for an end time shown next to a full start date-time). */
export function formatReportTime(value?: string | Date | null, locale?: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale || undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(date);
  } catch {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
  }
}

export interface DurationUnits {
  s: string;
  m: string;
  h: string;
  d: string;
}

/** Compact duration: 45s · 12m · 1h 05m · 2d 3h. Negative values (e.g. a
 * visit started before its scheduled time) keep their sign. */
export function formatReportDuration(totalSeconds: number | null | undefined, units: DurationUnits, opts?: { compact?: boolean }): string {
  if (totalSeconds === null || totalSeconds === undefined || !Number.isFinite(totalSeconds)) return '—';
  const sign = totalSeconds < 0 ? '-' : '';
  let rest = Math.round(Math.abs(totalSeconds));
  const days = Math.floor(rest / 86400); rest -= days * 86400;
  const hours = Math.floor(rest / 3600); rest -= hours * 3600;
  const minutes = Math.floor(rest / 60);
  const seconds = rest - minutes * 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  if (days > 0) return `${sign}${days}${units.d} ${hours}${units.h} ${pad(minutes)}${units.m}`;
  if (hours > 0) return opts?.compact ? `${sign}${hours}${units.h} ${pad(minutes)}${units.m}` : `${sign}${hours}${units.h} ${pad(minutes)}${units.m} ${pad(seconds)}${units.s}`;
  if (minutes > 0) return `${sign}${minutes}${units.m} ${pad(seconds)}${units.s}`;
  return `${sign}${seconds}${units.s}`;
}
